/**
 * Badge "Revisione di {parent name} (v{N})" — mostrato sotto il titolo
 * preventivo quando ha parent_estimate_id. Click → naviga al padre.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { GitBranch, ArrowUpRight } from 'lucide-react';

interface ParentEstimateBadgeProps {
  parentEstimateId: string | null | undefined;
  currentVersion: number;
}

const ParentEstimateBadge: React.FC<ParentEstimateBadgeProps> = ({ parentEstimateId, currentVersion }) => {
  const navigate = useNavigate();

  const { data: parent } = useQuery({
    queryKey: ['estimate-parent', parentEstimateId],
    queryFn: async () => {
      if (!parentEstimateId) return null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data } = await (supabase as any)
        .from('estimates')
        .select('id, name, version')
        .eq('id', parentEstimateId)
        .maybeSingle();
      return data;
    },
    enabled: !!parentEstimateId,
    staleTime: 5 * 60_000,
  });

  if (!parentEstimateId) return null;

  return (
    <button
      onClick={() => navigate(`/estimates/${parentEstimateId}/manage`)}
      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors group"
      title="Vai alla versione precedente"
    >
      <GitBranch className="h-3.5 w-3.5" />
      <span>
        Revisione v{currentVersion} di{' '}
        <span className="font-medium underline-offset-2 group-hover:underline">
          {parent?.name ?? 'preventivo precedente'}
        </span>
        {parent?.version ? <span className="text-muted-foreground/70"> (v{parent.version})</span> : null}
      </span>
      <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
};

export default ParentEstimateBadge;
